import { useState, type FormEvent } from "react";
import { contactData } from "@/mocks/homeData";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const name = (formData.get("name") as string || "").trim();
    const email = (formData.get("email") as string || "").trim();
    const phone = (formData.get("phone") as string || "").trim();
    const service = (formData.get("service") as string || "").trim();
    const message = (formData.get("message") as string || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setErrorMsg("Please fill in all required fields.");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setErrorMsg("Message must be 500 characters or less.");
      return;
    }

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("phone", phone);
    payload.append("service", service);
    payload.append("message", message);

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9no02obdkpk99j7gm70", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const code =
        typeof parsed?.code === "string" ? parsed.code : "";
      const serverMsg =
        (parsed as Record<string, unknown>)?.meta &&
        typeof (parsed as Record<string, Record<string, unknown>>).meta?.message === "string"
          ? ((parsed as Record<string, Record<string, unknown>>).meta.message as string)
          : "";
      const serverDetail =
        (parsed as Record<string, unknown>)?.meta &&
        typeof (parsed as Record<string, Record<string, unknown>>).meta?.detail === "string"
          ? ((parsed as Record<string, Record<string, unknown>>).meta.detail as string)
          : "";

      const msg = serverMsg || serverDetail || responseText;
      const isSpam =
        msg.toLowerCase().includes("spam") || msg.toLowerCase().includes("form data is spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setErrorMsg(msg || "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error ? err.message : "Network error. Please try again."
      );
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <span className="text-sm font-semibold text-primary-500 uppercase tracking-wider">
            Start a Conversation
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mt-2 mb-4">
            {contactData.headline}
          </h2>
          <p className="text-foreground-700 leading-relaxed">
            {contactData.subheadline}
          </p>
        </div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="w-full lg:w-2/5">
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground-950 mb-6">
                Contact Information
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-primary-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-600 mb-0.5">
                      Phone
                    </div>
                    <a
                      href={`tel:${contactData.info.phone.replace(/\s/g, "")}`}
                      className="text-foreground-950 font-medium hover:text-primary-500 transition-colors"
                    >
                      {contactData.info.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-primary-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-600 mb-0.5">
                      Email
                    </div>
                    <a
                      href={`mailto:${contactData.info.email}`}
                      className="text-foreground-950 font-medium hover:text-primary-500 transition-colors"
                    >
                      {contactData.info.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-primary-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-600 mb-0.5">
                      Address
                    </div>
                    <span className="text-foreground-950 font-medium">
                      {contactData.info.address}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-primary-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-600 mb-0.5">
                      Business Hours
                    </div>
                    <span className="text-foreground-950 font-medium">
                      {contactData.info.hours}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-3/5">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70"
            >
              <div className="honeypot-field">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Full Name <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Email <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    htmlFor="contact-phone"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-service"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Service Interest
                  </label>
                  <select
                    id="contact-service"
                    name="service"
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="call-recording">Call Recording Solutions</option>
                    <option value="compliance">Compliance Recording</option>
                    <option value="enterprise-software">Enterprise Software</option>
                    <option value="digital-transformation">Digital Transformation</option>
                    <option value="it-staffing">IT Staffing</option>
                    <option value="permanent-recruitment">Permanent Recruitment</option>
                    <option value="contract-staffing">Contract Staffing</option>
                    <option value="rpo">Recruitment Process Outsourcing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mb-5">
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  Message <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                  placeholder="Tell us about your project or staffing needs..."
                />
                <div className="text-xs text-foreground-500 mt-1 text-right">
                  Max 500 characters
                </div>
              </div>

              {status === "error" && errorMsg && (
                <div className="mb-4 p-3 rounded-md bg-accent-100 text-accent-900 text-sm">
                  {errorMsg}
                </div>
              )}
              {status === "success" && (
                <div className="mb-4 p-3 rounded-md bg-primary-100 text-primary-900 text-sm">
                  Thank you! We have received your message and will respond within one business day.
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
              >
                {status === "loading" ? "Sending..." : "Schedule a Consultation"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}